import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import type {
  AiPreviewData,
  BookingSlot,
  CampaignApiResponse,
  PreviewData,
  SlotsByDate,
} from '@/features/campaigns/types/campaign';

/** Matches backend `MAX_AVAILABLE_SLOT_DAYS` on get-available-slots. */
const MAX_AVAILABILITY_QUERY_DAYS = 15;

function chunkAvailabilityDates(dates: string[], size: number): string[][] {
  const sorted = [...dates].sort();
  const chunks: string[][] = [];
  for (let i = 0; i < sorted.length; i += size) {
    chunks.push(sorted.slice(i, i + size));
  }
  return chunks;
}

/**
 * Loads availability for the full date list. Backend caps each request at 15 days,
 * so longer campaigns are fetched in parallel chunks and merged.
 */
export async function fetchAvailableSlots(payload: {
  dates: string[];
  billboardIds: string[];
  customDuration: string | number;
}) {
  const { dates, billboardIds, customDuration } = payload;
  const chunks =
    dates.length > MAX_AVAILABILITY_QUERY_DAYS
      ? chunkAvailabilityDates(dates, MAX_AVAILABILITY_QUERY_DAYS)
      : [dates];

  if (chunks.length <= 1) {
    const { data } = await httpClient.post(endpoints.campaigns.availableSlots, {
      dates,
      billboardIds,
      customDuration,
    });
    return data as CampaignApiResponse<SlotsByDate>;
  }

  const responses = await Promise.all(
    chunks.map((chunk) =>
      httpClient.post(endpoints.campaigns.availableSlots, {
        dates: chunk,
        billboardIds,
        customDuration,
      }),
    ),
  );

  const merged: SlotsByDate = {};
  let success = true;
  let message: string | undefined;
  for (const res of responses) {
    const body = res.data as CampaignApiResponse<SlotsByDate>;
    if (body?.success === false) success = false;
    if (body?.message) message = body.message;
    Object.assign(merged, body?.data || {});
  }

  return {
    success,
    ...(message ? { message } : {}),
    data: merged,
  } as CampaignApiResponse<SlotsByDate>;
}

export async function campaignStep1(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(endpoints.campaigns.step1, payload);
  return data as CampaignApiResponse<{ campaignId?: string }>;
}

export async function campaignStep2(payload: {
  billboardIds: string[];
  campaignId: string;
}) {
  const { data } = await httpClient.post(endpoints.campaigns.step2, payload);
  return data as CampaignApiResponse<{ campaignId?: string }>;
}

export async function campaignStep3(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(endpoints.campaigns.step3, payload);
  return data as CampaignApiResponse<PreviewData>;
}

export async function campaignStep4(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(endpoints.campaigns.step4, payload);
  return data as CampaignApiResponse<{ walletId?: string; campaignId?: string }>;
}

export async function relaunchCampaign(payload: {
  campaignId: string;
  name?: string;
  startDate: string;
  endDate: string;
  bookingSlots: BookingSlot[];
  qrInfo?: Record<string, unknown>;
  couponCode?: string;
}) {
  const { data } = await httpClient.post(endpoints.campaigns.relaunch, payload);
  return data as CampaignApiResponse<PreviewData>;
}

export async function aiGeneratePreview(payload: Record<string, unknown>) {
  const { data } = await httpClient.post(
    endpoints.campaigns.aiGeneratePreview,
    payload,
    { timeout: 60_000 },
  );
  return data as CampaignApiResponse<AiPreviewData>;
}

/** Free plays per hour for each screen on the given dates: `{ [screenId]: { [hour]: freePlays } }`. */
export async function fetchScreenCapacity(payload: {
  billboardIds: string[];
  dates: string[];
  customDuration: number;
}) {
  const { data } = await httpClient.post(endpoints.campaigns.aiScreenCapacity, payload);
  return data as CampaignApiResponse<{ capacity?: Record<string, Record<string, number>> }>;
}

/** How long the user's server-side hold on a campaign's slots has left (server clock). */
export async function fetchSlotHold(campaignId: string) {
  const { data } = await httpClient.get(endpoints.campaigns.slotHold, { params: { campaignId } });
  return data as CampaignApiResponse<{
    campaignId?: string;
    active?: boolean;
    /** False when the server could not read its hold store (unknown, not expired). */
    available?: boolean;
    expiresAt?: string | null;
    secondsLeft?: number;
    serverTime?: string;
  }>;
}

export async function saveAiCampaign(payload: {
  agencyClientId?: string;
  campaignId: string;
  campaignName?: string;
  couponCode?: string;
  galleryId?: string;
  mediaIds?: Array<{
    mediaId: string;
    orientation?: string;
    mediaType?: string;
  }>;
}) {
  const { data } = await httpClient.post(endpoints.campaigns.saveCampaign, payload);
  return data as CampaignApiResponse<{ campaignId?: string; walletId?: string }>;
}

export async function updateCampaignContent(payload: {
  campaignId: string;
  mediaIds?: Array<{
    mediaId: string;
    orientation: 'PORTRAIT' | 'LANDSCAPE';
  }>;
  galleryId?: string;
}) {
  const { data } = await httpClient.post(
    endpoints.campaigns.updateContent,
    payload,
  );
  return data as CampaignApiResponse<unknown>;
}

export async function fetchMyCampaigns(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  agencyClientId?: string;
}) {
  const {
    page = 1,
    limit = 15,
    search = '',
    status = '',
    startDate = '',
    endDate = '',
    agencyClientId = '',
  } = params;

  const query: Record<string, string | number> = { page, limit, search };
  if (status && status !== 'All') query.status = status;
  if (startDate) query.startDate = startDate;
  if (endDate) query.endDate = endDate;
  if (agencyClientId) query.agencyClientId = agencyClientId;

  const { data } = await httpClient.get(endpoints.campaigns.list, {
    params: query,
  });
  return data as {
    message?: string;
    data?: {
      campaigns?: CampaignListItem[];
      pagination?: {
        totalPages?: number;
        totalCount?: number;
        total?: number;
        page?: number;
        limit?: number;
      };
    };
  };
}

export type CampaignListItem = {
  footfallCount: string;
  campaignId?: string;
  _id?: string;
  campaignName?: string;
  name?: string;
  orderId?: string;
  status?: string;
  bookingDate?: string;
  duration?: string | number;
  customDuration?: string | number;
  adDuration?: string | number;
  startDate?: string;
  thumbnail?: string;
  analytics?: {
    customDuration?: string | number;
    totalDuration?: string | number;
    totalSlots?: number;
    totalRepetitions?: number;
  } | null;
  bookedSlots?: Array<{ duration?: string | number }>;
  totalSlots?: number;
  totalViews?: number;
  media?: { url?: string; mediaType?: string };
  agencyClientId?: string;
  agencyClientName?: string;
  clientName?: string;
  agencyClient?: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
  };
};

export type BillboardCityOption = {
  city?: string;
  state?: string;
  screenCount?: number;
  /** `available` = live screens; `upcoming` = coming soon / onboarding */
  status?: string;
};

export async function fetchBillboardCities(
  search = '',
  page = 1,
  limit = 20,
) {
  const { data } = await httpClient.get(endpoints.locations.cities, {
    params: { search, page, limit, includeComingSoon: true },
  });
  return data as {
    data?: BillboardCityOption[];
    message?: string;
    hasMore?: boolean;
    pagination?: { hasMore?: boolean; page?: number; totalPages?: number };
  };
}


export async function fetchStatusCount() {
  const { data } = await httpClient.get(endpoints.campaigns.statusCount);
  return data
}